import { htmlDecode } from '../utils/general';

export default class YotpoReviewTracker {
  constructor(productTitle) {
    this.productTitle = productTitle;
    this.containerEl = document.querySelector('.product-review__container');
    this.yotpoElementSelector = '[data-yotpo-main-widget]';

    this.waitForYotpo().then(() => {
      this.init();
    });
  }

  init() {
    if (!this.containerEl) return;
    this.trackFiltering();
    this.trackReviewSorting();
    this.trackReviewPagination();
    this.trackReviewSearching();
    this.trackReviewInitiating();
    this.trackReviewSubmit();
  }

  async waitForYotpo() {
    const MAX_RETRIES = 20;
    let retries = 0;

    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (window?.yotpo && window.yotpo.state === 'ready') {
          clearInterval(interval);
          resolve();
        } else {
          retries += 1;
          if (retries >= MAX_RETRIES) {
            clearInterval(interval);
            resolve();
          }
        }
      }, 200);
    });
  }

  trackEvent(param) {
    window.dataLayer.push(param);
  }

  trackFiltering() {
    this.filterElements = this.containerEl.querySelectorAll(
      '.dropdown-filters-container li.list-category',
    );
    this.filterElements.forEach((el) =>
      el.addEventListener('click', this.trackReviewFilteringDesktop.bind(this)),
    );

    this.mobileFilterModalOpenButton = document.querySelector(
      '.yotpo-icon-btn.more-filters-btn',
    );
    this.mobileFilterModalOpenButton?.addEventListener(
      'click',
      this.trackReviewFilteringMobile.bind(this),
    );
  }

  trackReviewFilteringMobile() {
    const mobileFilterModal = document.querySelector(
      '.yotpo.mobile-filters-modal',
    );
    const mobileFilterSubmitButton = mobileFilterModal.querySelector(
      '.mobile-filters-footer-btn',
    );

    const handleSubmit = () => {
      const fieldsets = mobileFilterModal.querySelectorAll('fieldset');
      fieldsets.forEach((fieldset) => {
        const filterName = fieldset.querySelector('legend').textContent;
        const filterValue =
          fieldset.querySelector('input:checked')?.dataset?.value;

        const params = {
          event: 'review_filter',
          filter_name: filterName,
          filter_value: filterValue,
          product_name: htmlDecode(this.productTitle),
        };
        this.trackEvent(params);
      });
      mobileFilterSubmitButton.removeEventListener('click', handleSubmit);
    };

    mobileFilterSubmitButton.addEventListener('click', handleSubmit);
  }

  trackReviewFilteringDesktop(event) {
    const { target } = event;
    const filterName =
      target.closest('.filters-dropdown')?.dataset?.defaultButtonDisplayValue;
    const filterValue = target.closest('.list-category').dataset?.value;
    const params = {
      event: 'review_filter',
      filter_name: filterName,
      filter_value: filterValue,
      product_name: htmlDecode(this.productTitle),
    };
    this.trackEvent(params);
  }

  trackReviewSorting() {
    const optionSelector = '.sort-drop-down .list-category';
    const options = this.containerEl.querySelectorAll(optionSelector);
    options.forEach((option) =>
      option.addEventListener('click', (event) => {
        const value =
          event.target.closest('.list-category').dataset?.analyticProperty;
        const params = {
          event: 'review_sort',
          sort_by: value,
          product_name: htmlDecode(this.productTitle),
        };

        this.trackEvent(params);
      }),
    );
  }

  trackReviewPagination() {
    // Re-bind event listeners after pagination is clicked
    // This is because the pagination is re-rendered after clicking
    const clickedItems = [];
    const bindEventListeners = () => {
      this.containerEl = document.querySelector('.product-review__container');
      const paginationSelector = '.yotpo-page-element';
      const paginationElements =
        this.containerEl.querySelectorAll(paginationSelector);

      paginationElements.forEach((paginationElement) => {
        paginationElement.addEventListener('click', (event) => {
          let value = event.target.closest('.yotpo-page-element').dataset?.page;

          if (!value) {
            value =
              event.target.ariaLabel.toLowerCase() === 'next page'
                ? 'next'
                : 'previous';
          }

          if (value && !clickedItems.includes(value)) {
            const params = {
              event: 'review_scroll',
              click_text: value,
              product_name: htmlDecode(this.productTitle),
            };

            this.trackEvent(params);
            clickedItems.push(value);
          }

          setTimeout(() => {
            bindEventListeners();
          }, 800);
        });
      });
    };

    bindEventListeners();
  }

  trackReviewSearching() {
    const selector = '.free-text-search-input';
    const searchInput = this.containerEl.querySelector(selector);
    searchInput?.addEventListener('keyup', (event) => {
      if (event.key === 'Enter') {
        const params = {
          event: 'review_search',
          search_term: event.target.value,
          product_name: htmlDecode(this.productTitle),
        };

        this.trackEvent(params);
      }
    });
  }

  trackReviewInitiating() {
    const selector = '.yotpo-default-button.write-review-button';
    let writeReviewButtons = this.containerEl.querySelectorAll(selector);
    if (writeReviewButtons.length == 0) {
      writeReviewButtons = this.containerEl.querySelectorAll(
        '.yotpo-new-review-btn',
      );
    }
    writeReviewButtons?.forEach((btn) =>
      btn.addEventListener('click', () => {
        const params = {
          event: 'review_initiate',
          product_name: htmlDecode(this.productTitle),
        };

        this.trackEvent(params);
      }),
    );
  }

  trackReviewSubmit() {
    const isNewWidget = this.containerEl.querySelector('.yotpo-widget-instance')
      ? true
      : false;

    const payload = {
      event: 'review_submit',
      product_name: htmlDecode(this.productTitle),
    };
    const config = {
      quality_rating: 'How would you rate the quality of this product',
      product_value: 'How would you rate the value of this product?',
      product_love_reason: 'Why do you love this product ?',
      age_range: 'What is your age range',
      gender: 'What is your gender?',
      skin_type: 'What is your skin type?',
      product_recommendation: 'Would you recommend this product to a friend?',
      skin_tone: 'What is your skin tone?',
    };

    if (isNewWidget) {
      const mainWidget = document.querySelector(
        '.yotpo-reviews-main-widget:not(#yotpo-reviews-main-widget)',
      );
      if (mainWidget) {
        const _this = this;
        const observer = new MutationObserver(function (mutations) {
          if (mutations[0].type === 'childList') {
            // Check if modal is visible
            const isModalVisible =
              mainWidget.querySelector('.yotpo-modal') != null;

            if (isModalVisible) {
              const submitButton = mainWidget.querySelector(
                '.yotpo-new-review-submit',
              );

              submitButton.addEventListener('click', () => {
                payload.score = document.querySelectorAll(
                  '.yotpo-review-form .yotpo-star-rating-icon.yotpo-sr-star-full',
                ).length;

                for (const [key, text] of Object.entries(config)) {
                  const spans = Array.from(
                    document.querySelectorAll(
                      '.yotpo-custom-question-headline',
                    ),
                  );

                  // Find the span with the specified text
                  const span = spans.find((span) =>
                    span.innerText.trim().includes(text),
                  );
                  let answer = '';
                  if (span) {
                    const formElement = span.parentElement;
                    const options = formElement.querySelectorAll(
                      '.yotpo-custom-questions-option',
                    );
                    const choices = [];
                    options.forEach((option) => {
                      if (
                        option.style.backgroundColor == 'var(--primary-color)'
                      ) {
                        choices.push(option.textContent);
                      }
                    });
                    answer = choices.join(',');
                  }

                  payload[key] = answer;
                }

                window.dataLayer.push(payload);
              }, { capture: true });
            }
          }
        });

        // Start observing the modal for attribute changes
        observer.observe(mainWidget, {
          childList: true,
        });
      }
    } else {
      const selector = '.yotpo-default-button.yotpo-submit';
      let submitButton = this.containerEl.querySelector(selector);

      submitButton?.addEventListener('click', () => {
        // score
        const stars = this.containerEl.querySelectorAll(
          '.write-review .stars-wrapper .yotpo-icon[aria-checked="true"]',
        );
        payload.score = stars[stars.length - 1].dataset.score;

        // Inner texts of each review radio questions

        // Set the payload properties
        for (const [key, text] of Object.entries(config)) {
          const spans = Array.from(
            this.containerEl.querySelectorAll(
              '.write-review .y-label.yotpo-field-title',
            ),
          );

          // Find the span with the specified text
          const span = spans.find((span) => span.innerText.trim() === text);
          let answer = '';
          if (span) {
            const formElement = span.parentElement;

            const radioElement = formElement.querySelector(
              'input[type="radio"]:checked',
            );
            const selectElement = formElement.querySelector('select');
            const checkboxElements = formElement.querySelectorAll(
              'input[type="checkbox"]:checked',
            );

            if (radioElement) {
              answer = radioElement.value;
            }
            if (selectElement) {
              answer = selectElement.value;
            }
            if (checkboxElements && checkboxElements.length > 0) {
              const choices = [];
              checkboxElements.forEach((item) => choices.push(item.value));
              answer = choices.join(',');
            }
          }

          payload[key] = answer;
        }

        this.trackEvent(payload);
      });
    }
  }
}
